<template>

    <div id="create-edit-schedule" role="main">

        <div class="flex">
            <div class="flex-1 mb-4 text-2xl font-medium text-gray-900 capitalize">
                <h1>Report Schedule</h1>
            </div>
        </div>

        <ul class="top-help-block">
            <li><span class="text-brand-700">*</span> denotes required field</li>
        </ul>

        <form action="#" method="POST" @submit.prevent="submitForm" v-if="loaded">

            <div class="panel">
                <div class="panel-form">
                    <div class="panel-title">
                        <h3>Report</h3>
                        <p>
                            Select the report you would like to run on a schedule.
                        </p>
                    </div>
                    <div class="panel-section">

                        <div class="grid grid-cols-6 gap-6">
                            <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('report_id') }">
                                <label for="report_id" class="block text-sm font-medium text-gray-700">Report<i></i></label>

                                <select id="report_id" v-model="form.data.report_id" required @change="selectReport">
                                    <option :value="null">Select Report</option>
                                    <option :value="report.id" v-for="report in form.reports.data">{{ report.name }}</option>
                                </select>
                                <span v-if="form.fieldHasErrors('report_id')">{{ form.getFieldError('report_id') }}</span>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <div class="panel">
                <div class="panel-form">
                    <div class="panel-title">
                        <h3>Report Parameters</h3>
                        <p>
                            Select the report parameters that will be run against the report (any date and time fields will
                            be automatically handled by the report schedule frequency).
                        </p>
                    </div>
                    <div class="panel-section">

                        <div v-if="form.reportSelected()">
                            <ReportParameters v-model="form" :show_date_fields="false">
                                <template v-slot:disabled-help-text>
                                    This field will be automatically handled by the report schedule frequency.
                                </template>
                            </ReportParameters>
                        </div>
                        <div class="" v-else>
                            <div class="p-10">
                                <div class="text-gray-200 my-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-20 w-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>

                                <h2 class="text-gray-600 font-medium text-xl text-center">Select Report</h2>

                                <p class="text-center mt-4 text-sm text-gray-500">
                                    You must select a report first before being able to edit parameters.
                                </p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <div class="panel">
                <div class="panel-form">
                    <div class="panel-title">
                        <h3>Schedule</h3>
                        <p>
                            Setup the schedule for this report to run against, please note that reports are run at designated
                            periods depending on the frequency selected (e.g. Daily at 00:00:00Z, Weekly Monday at 00:00:00Z,
                            Monthly 01st of Month at 00:00:00Z) subtracting the relevant date (e.g. Daily runs between
                            00:00:00Z to 23:59:59Z for the previous day).
                        </p>
                    </div>
                    <div class="panel-section">

                        <div class="grid grid-cols-6 gap-6">
                            <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('frequency') }">
                                <label for="frequency" class="block text-sm font-medium text-gray-700">Frequency<i></i></label>

                                <select id="frequency" v-model="form.data.frequency" required>
                                    <option :value="frequency.id" v-for="frequency in form.schedule_freqencies">{{ frequency.name }}</option>
                                </select>
                                <span v-if="form.fieldHasErrors('frequency')">{{ form.getFieldError('frequency') }}</span>

                                <p class="text-sm text-gray-400 mt-2">
                                    How often the report will be run.
                                </p>
                            </div>

                            <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('recipients') }">
                                <label for="recipients" class="block text-sm font-medium text-gray-700">Recipients<i></i></label>
                                <input type="text" name="recipients" id="recipients" v-model="form.data.recipients">
                                <span v-if="form.fieldHasErrors('recipients')">{{ form.getFieldError('recipients') }}</span>

                                <p class="text-sm text-gray-400 mt-2">
                                    A list of recipients that report results will be emailed to (comma separated <span class="font-semibold">e.g. j.doe@example.org, finance@example.org, tech@example.org</span>)
                                </p>
                            </div>

                        </div>

                    </div>
                </div>
            </div>

            <div class="flex mt-6 justify-end">

                <div class="flex border-l-4 border-brand-500 bg-white text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-brand-400" v-if="form.successful()">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p class="text-gray-700 text-sm" v-html="form.getSuccessMessage()"></p>
                </div>

                <div class="flex border-l-4 border-error-500 bg-error-50 text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-error-400" v-if="form.hasError()">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <p class="text-error-500 text-sm" v-html="form.getErrorMessage()"></p>
                </div>

                <button type="submit" class="btn-outline justify-center px-6 ml-6" @click.prevent="goBack">
                    Go Back
                </button>

                <button type="submit" class="btn justify-center px-6 ml-6" :disabled="form.isFormUpdating()">
                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" v-if="form.isFormUpdating()">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ formButtonText }}
                </button>
            </div>

        </form>
        <Loading v-else></Loading>

    </div>

</template>

<script>
import Loading from "../Support/Loading";
import {Report} from "../../app/Report/Report";
import {ReportSchedule} from "../../app/Report/ReportSchedule";
import ReportParameters from "./ReportParameters";
import {NavigationMixin} from "../../support/mixins";

export default {
    name: "CreateEditSchedule",
    mixins: [NavigationMixin],
    components: {
        ReportParameters,
        Loading
    },
    data() {
        return {
            uuid: null,
            form: false,
            loaded: false,
        }
    },
    computed: {
        formButtonText() {
            if (this.uuid !== null) {
                return 'Edit Schedule';
            }

            return 'Create Schedule';
        }
    },
    methods: {
        selectReport() {
            let vm = this;

            vm.form.showReport(true);
        },
        submitForm() {
            let vm = this;

            if (vm.uuid !== null) {
                vm.form.updateScheduledReport(vm.uuid).then(r => {
                    vm.$router.push({ name: 'report' });
                });
            } else {
                vm.form.createScheduledReport().then(r => {
                    vm.$router.push({ name: 'report' });
                });
            }
        },
    },
    mounted() {
        let vm = this;

        new Promise(resolve => {

            vm.uuid = vm.$route.params.schedule ?? null;

            new Report().all().then(reports => {
                vm.form = new ReportSchedule(reports, {
                    report_id: null,
                    parameters: {},
                    frequency: 'weekly',
                    limit: null,
                });

                if (vm.uuid !== null) {
                    Promise.all([
                        vm.form.showScheduledReport(vm.uuid),
                        vm.form.getScheduleFrequencies()
                    ]).then(r => {
                        resolve(true);
                    });
                } else {
                    vm.form.getScheduleFrequencies().then(r => {
                        resolve(true);
                    });
                }
            });
        }).then(r => {
            vm.loaded = true;
        });
    }
}
</script>